import React, { useEffect, useState } from 'react';
import { Save, ExternalLink, Send } from 'lucide-react';
import { apiCall, fetchAdmin } from './_helpers';

const FIELD_HELP = {
  meta_pixel_id: {
    label: 'Meta Pixel ID',
    placeholder: '15- or 16-digit ID',
    help: 'Business Manager → Events Manager → Data Sources → your Pixel → Settings. Looks like 123456789012345.',
    url: 'https://business.facebook.com/events_manager',
  },
  meta_capi_token: {
    label: 'Meta Conversions API Access Token',
    placeholder: 'long token (kept secret on backend)',
    help: 'Same page → "Set up Conversions API" → "Generate access token". Stored backend-only.',
    url: 'https://business.facebook.com/events_manager',
    secret: true,
  },
  meta_test_event_code: {
    label: 'Meta CAPI Test Event Code (optional)',
    placeholder: 'TESTxxxxx',
    help: 'Events Manager → Test Events tab. Set this temporarily to send events into the Test tab instead of production.',
  },
  meta_domain_verification: {
    label: 'Meta Domain Verification Code (optional)',
    placeholder: 'value of the verification meta tag',
    help: 'Business Settings → Brand Safety → Domains → Verify by meta tag. Paste just the verification code value.',
    url: 'https://business.facebook.com/business/help/286768115176155',
  },
  ga4_measurement_id: {
    label: 'Google Analytics 4 Measurement ID',
    placeholder: 'G-XXXXXXXXXX',
    help: 'Analytics → Admin → Data Streams → Web → your stream. Always starts with G-.',
    url: 'https://analytics.google.com/',
  },
  google_ads_id: {
    label: 'Google Ads Conversion ID',
    placeholder: 'AW-XXXXXXXXX',
    help: 'Google Ads → Tools → Conversions → click any conversion → tag setup → "Global site tag". Starts with AW-.',
    url: 'https://ads.google.com/',
  },
  google_ads_reservation_label: {
    label: 'Google Ads "Reserve" Conversion Label (optional)',
    placeholder: 'abc123XYZ',
    help: 'Create a Google Ads conversion action for reservations, then paste its label (the part after the slash in send_to).',
  },
  google_ads_lead_label: {
    label: 'Google Ads "Lead" Conversion Label (optional)',
    placeholder: 'abc123XYZ',
    help: 'Create a Google Ads conversion action for private event leads. Paste its label.',
  },
  tiktok_pixel_id: {
    label: 'TikTok Pixel ID',
    placeholder: '20-character ID',
    help: 'TikTok Ads Manager → Tools → Events → Web Events → your Pixel → Settings.',
    url: 'https://ads.tiktok.com/i18n/events_manager/',
  },
  posthog_key: {
    label: 'PostHog Project API Key (optional)',
    placeholder: 'phc_xxxxxxxx',
    help: 'PostHog → Project Settings → Project API Key. Leave blank to use the default key already in index.html.',
  },
  posthog_host: {
    label: 'PostHog Host (optional)',
    placeholder: 'https://us.i.posthog.com',
    help: 'Most users keep the default. EU customers might use https://eu.i.posthog.com.',
  },
  behold_feed_id: {
    label: 'Behold.so Instagram Feed ID',
    placeholder: 'H4qhSp3EbYPvxLK2vGGm',
    help: 'behold.so → your feed → Widget tab → copy the Feed ID. Powers the homepage Instagram Reel Wall + Marquee.',
    url: 'https://behold.so',
  },
};

const ORDER = [
  ['Meta (Facebook / Instagram)', ['meta_pixel_id', 'meta_capi_token', 'meta_test_event_code', 'meta_domain_verification']],
  ['Google Analytics & Ads', ['ga4_measurement_id', 'google_ads_id', 'google_ads_reservation_label', 'google_ads_lead_label']],
  ['TikTok', ['tiktok_pixel_id']],
  ['PostHog (already loaded sitewide)', ['posthog_key', 'posthog_host']],
  ['Instagram Feed (Behold.so)', ['behold_feed_id']],
];

const MarketingManager = ({ onMessage }) => {
  const [cfg, setCfg] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchAdmin('marketing-config').then((d) => setCfg(d || {}));
  }, []);

  if (!cfg) return <div className="text-gray-500">Loading…</div>;

  const updateField = (key, value) => setCfg({ ...cfg, [key]: value });

  const handleSave = async () => {
    try {
      await apiCall('PUT', 'marketing-config', cfg);
      onMessage('success', 'Marketing config saved. Frontend will pick up new IDs on next page load.');
    } catch (e) { onMessage('error', e.message); }
  };

  const handleTestCapi = async () => {
    if (!cfg.meta_pixel_id || !cfg.meta_capi_token) {
      onMessage('error', 'Add Meta Pixel ID + CAPI token first.');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await apiCall('POST', 'marketing-config/test-capi', {
        event_name: 'PageView',
        email: testEmail || undefined,
        source_url: window.location.origin,
      });
      setTestResult(r.result);
      const ok = r.result?.events_received >= 1;
      onMessage(ok ? 'success' : 'error', ok ? `Sent. fbtrace_id: ${r.result.fbtrace_id || '—'}` : 'Sent but no events accepted — check token/Pixel ID.');
    } catch (e) {
      setTestResult({ error: e.message });
      onMessage('error', e.message);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold">Marketing & Analytics</h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Paste tracking IDs from Meta, Google, and TikTok below. Each tracker is dormant until its ID is filled in.
            Meta Pixel + Conversions API run side-by-side for best ad attribution. Server-side events are deduped by event_id.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm bg-white border rounded-lg px-3 py-2">
          <input
            type="checkbox"
            checked={cfg.enabled !== false}
            onChange={(e) => updateField('enabled', e.target.checked)}
            data-testid="marketing-enabled-toggle"
          />
          Tracking enabled
        </label>
      </div>

      {ORDER.map(([group, fields]) => (
        <div key={group} className="bg-white rounded-xl p-6 shadow-md mb-6">
          <h3 className="text-lg font-semibold mb-4">{group}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {fields.map((key) => {
              const meta = FIELD_HELP[key];
              return (
                <div key={key} className={fields.length === 1 ? 'md:col-span-2' : ''}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {meta.label}
                    {meta.secret && <span className="ml-2 text-[10px] text-amber-600 font-semibold uppercase tracking-wide">Secret</span>}
                  </label>
                  <input
                    type={meta.secret ? 'password' : 'text'}
                    value={cfg[key] || ''}
                    onChange={(e) => updateField(key, e.target.value)}
                    placeholder={meta.placeholder}
                    className="w-full border rounded-lg px-3 py-2 font-mono text-sm"
                    data-testid={`marketing-input-${key}`}
                  />
                  <div className="flex items-start gap-2 mt-1 text-xs text-gray-500">
                    <span>{meta.help}</span>
                    {meta.url && (
                      <a href={meta.url} target="_blank" rel="noopener noreferrer" className="text-[#C9A86C] hover:underline whitespace-nowrap inline-flex items-center gap-1">
                        Open <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="flex flex-wrap gap-3 mb-8">
        <button
          onClick={handleSave}
          className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg flex items-center gap-2"
          data-testid="marketing-save-btn"
        >
          <Save size={18} /> Save
        </button>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-md">
        <h3 className="text-lg font-semibold mb-2">Test Meta CAPI</h3>
        <p className="text-sm text-gray-500 mb-4">
          Send a sandbox PageView to Meta to confirm your Pixel ID + token work. If you've set a
          Test Event Code, the event appears in Events Manager → Test Events. Otherwise it appears in
          live event stream after ~20 minutes.
        </p>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-sm text-gray-600 mb-1">Test email (optional, gets hashed)</label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <button
            onClick={handleTestCapi}
            disabled={testing}
            className="bg-[#1a1a1a] hover:bg-black disabled:opacity-50 text-white px-5 py-2.5 rounded-lg flex items-center gap-2"
            data-testid="marketing-test-capi-btn"
          >
            <Send size={18} /> {testing ? 'Sending…' : 'Send test PageView'}
          </button>
        </div>
        {testResult && (
          <pre className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs overflow-x-auto">
            {JSON.stringify(testResult, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
};

export default MarketingManager;
